import React, { useCallback, useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Legend,
} from 'recharts';
import { Loader2, RefreshCw, AlertCircle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, GUEST_TOKEN_KEY } from '../context/AuthContext';
import {
  avgLegendForDashboardRange,
  DashboardChartRange,
  labelForDashboardRange,
} from '../lib/dashboardChartRange';

type ChartMode = 'total' | 'rate';

const MODE_STORAGE_KEY = 'emailcraft_unsub_chart_mode';

interface UnsubMonthStat {
  month: string;
  label: string;
  count: number;
  sends: number;
  recipients: number;
  rate: number | null;
  partial: boolean;
}

interface UnsubMonthlyStatsResult {
  months: UnsubMonthStat[];
  averageCount: number | null;
  averageRate: number | null;
  chartMonths: number;
  range?: DashboardChartRange;
  rangeLabel?: string;
  granularity?: 'month' | 'day' | 'hour';
  cachedAt: string;
  excludedDates: string[];
  excludedCount: number;
  sendDrilldownEnabled?: boolean;
  fromCache?: boolean;
  error?: string;
}

interface ElevatedSend {
  statid: number;
  campaignName: string;
  unsubscribes: number;
  sendSize: number;
  rate: number;
  pctOfMonth: number;
  elevatedRate: boolean;
  baselineRate: number | null;
  zScore: number | null;
}

interface ElevatedSendsResult {
  month: string;
  label: string;
  monthTotal: number;
  attributedTotal: number;
  unattributedCount: number;
  baselineRate: number | null;
  thresholdRate: number | null;
  peerCount: number;
  topVolumeShare: number;
  sends: ElevatedSend[];
  message?: string;
}

function fmtCount(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 1 });
}

function fmtPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${Number(n).toFixed(2)}%`;
}

function readStoredMode(): ChartMode {
  try {
    const v = localStorage.getItem(MODE_STORAGE_KEY);
    return v === 'rate' ? 'rate' : 'total';
  } catch {
    return 'total';
  }
}

function CustomTooltip({ active, payload, drilldownEnabled, mode }: any) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as UnsubMonthStat & { avgLine?: number | null; primary?: number | null };
  if (!row) return null;
  return (
    <div className="rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 shadow-lg text-xs space-y-1 min-w-[170px]">
      <div className="font-semibold text-gray-900 dark:text-white">
        {row.label}
        {row.partial ? <span className="ml-1 font-normal text-amber-600">(MTD)</span> : null}
      </div>
      <div className="text-gray-600 dark:text-slate-300">
        Unsubscribes: <span className="font-medium tabular-nums">{fmtCount(row.count)}</span>
      </div>
      <div className="text-gray-600 dark:text-slate-300">
        Rate:{' '}
        <span className="font-medium tabular-nums">
          {fmtPct(row.rate)}
          {row.recipients > 0 && (
            <span className="font-normal text-gray-400">
              {' '}(of {fmtCount(row.recipients)} recipients)
            </span>
          )}
        </span>
      </div>
      <div className="text-gray-600 dark:text-slate-300">
        Sends: <span className="font-medium tabular-nums">{fmtCount(row.sends ?? 0)}</span>
      </div>
      {drilldownEnabled && (
        <div className="text-gray-400 dark:text-slate-500 text-[11px]">
          Click for top sends by volume
        </div>
      )}
      {mode === 'rate' && row.recipients === 0 && (
        <div className="text-amber-600 dark:text-amber-400 text-[11px]">
          No recipient volume this month — rate not plotted
        </div>
      )}
    </div>
  );
}

export default function UnsubscribeTrendChart({ range }: { range: DashboardChartRange }) {
  const { canAccess, user } = useAuth();
  const guestToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(GUEST_TOKEN_KEY) : null;
  const [data, setData] = useState<UnsubMonthlyStatsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<ChartMode>(() => readStoredMode());
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<ElevatedSendsResult | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState<string | null>(null);

  const setModePersist = (next: ChartMode) => {
    setMode(next);
    try { localStorage.setItem(MODE_STORAGE_KEY, next); } catch { /* ignore */ }
  };

  const load = useCallback(
    async (refresh = false, signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      setSelectedMonth(null);
      setDrilldown(null);
      try {
        const res = await axios.get<UnsubMonthlyStatsResult>('/api/interspire/unsubscribes/monthly-stats', {
          params: {
            range,
            ...(refresh ? { refresh: '1' } : {}),
          },
          signal,
        });
        if (signal?.aborted) return;
        setData(res.data);
        if (res.data.error) setError(res.data.error);
      } catch (e: any) {
        if (axios.isCancel?.(e) || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || signal?.aborted) {
          return;
        }
        setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load unsubscribe stats');
        // Do not clear prior series — aborted/stale range switches were blanking the chart.
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [range]
  );

  useEffect(() => {
    const ac = new AbortController();
    void load(false, ac.signal);
    return () => ac.abort();
  }, [load]);

  const isMonthlyGranularity = (data?.granularity ?? 'month') === 'month';

  const drilldownEnabled =
    isMonthlyGranularity &&
    (data?.sendDrilldownEnabled === true ||
      (!!user && canAccess('dashboardUnsubDrilldown')));

  const openDrilldown = useCallback(async (month: string) => {
    if (!drilldownEnabled) return;
    if (!/^\d{4}-\d{2}$/.test(month)) return;
    setSelectedMonth(month);
    setDrillLoading(true);
    setDrillError(null);
    setDrilldown(null);
    try {
      const res = await axios.get<ElevatedSendsResult>(
        `/api/interspire/unsubscribes/monthly-stats/${encodeURIComponent(month)}/elevated-sends`
      );
      setDrilldown(res.data);
    } catch (e: any) {
      setDrillError(e?.response?.data?.error ?? e?.message ?? 'Failed to load send breakdown');
    } finally {
      setDrillLoading(false);
    }
  }, [drilldownEnabled]);

  const averageValue = mode === 'rate' ? (data?.averageRate ?? null) : (data?.averageCount ?? null);

  const chartRows = (data?.months ?? []).map((m) => ({
    ...m,
    primary: mode === 'rate' ? (m.recipients > 0 ? m.rate : null) : m.count,
    avgLine: averageValue,
  }));

  const sendsLink = (statid: number) => {
    const base = `/sends`;
    const qs = guestToken ? `?gt=${encodeURIComponent(guestToken)}` : '';
    return `${base}${qs}${qs ? '&' : '?'}highlight=${statid}`;
  };

  const avgLabel =
    mode === 'rate'
      ? fmtPct(data?.averageRate)
      : fmtCount(data?.averageCount);

  const rangeLabel = data?.rangeLabel ?? labelForDashboardRange(range);
  const avgLegend = avgLegendForDashboardRange(range);
  const showInitialSpinner = loading && !data;
  const showFatalError = !loading && error && !(data?.months?.length);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white min-w-0">
          Interspire Unsubscribes
          {data && (
            <span className="font-semibold">
              {' | '}
              {rangeLabel} rolling average {avgLabel}
              {data.cachedAt && (
                <>
                  {' - synced '}
                  {new Date(data.cachedAt).toLocaleDateString('en-US')}
                </>
              )}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div
            className="inline-flex rounded-md border border-gray-200 dark:border-slate-600 p-0.5 text-xs"
            role="group"
            aria-label="Chart metric"
          >
            <button
              type="button"
              onClick={() => setModePersist('total')}
              className={`px-2.5 py-1 rounded ${
                mode === 'total'
                  ? 'bg-amber-500 text-white'
                  : 'text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              Total
            </button>
            <button
              type="button"
              onClick={() => setModePersist('rate')}
              className={`px-2.5 py-1 rounded ${
                mode === 'rate'
                  ? 'bg-amber-500 text-white'
                  : 'text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
              title="Unsubscribes ÷ recipients sent that month"
            >
              %
            </button>
          </div>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
            title="Refresh unsubscribe counts from Interspire"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {showInitialSpinner ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
        </div>
      ) : showFatalError ? (
        <div className="px-4 py-10 text-center space-y-2">
          <AlertCircle className="h-6 w-6 text-amber-500 mx-auto" />
          <p className="text-sm text-gray-600 dark:text-slate-300">{error}</p>
        </div>
      ) : (
        <div className="p-4 relative">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-slate-800/50 rounded-b-lg">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
            </div>
          )}
          {error && (
            <div className="mb-3 flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
              {error}
            </div>
          )}
          <div className="h-72 w-full min-w-0">
            <ResponsiveContainer key={range} width="100%" height="100%" minWidth={0}>
              <LineChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#37415133" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  interval="preserveStartEnd"
                  minTickGap={16}
                />
                <YAxis
                  yAxisId="unsubs"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) =>
                    mode === 'rate' ? `${Number(v).toFixed(1)}%` : Number(v).toLocaleString()
                  }
                  width={mode === 'rate' ? 48 : 56}
                  domain={
                    mode === 'rate'
                      ? [0, (dataMax: number) => Math.max(0.5, Math.ceil(dataMax * 1.15 * 10) / 10)]
                      : [0, (dataMax: number) => Math.max(10, Math.ceil(dataMax * 1.15))]
                  }
                  allowDecimals={mode === 'rate'}
                />
                <YAxis
                  yAxisId="sends"
                  orientation="right"
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  tickFormatter={(v) => Number(v).toLocaleString()}
                  width={32}
                  domain={[0, (dataMax: number) => Math.max(4, Math.ceil(dataMax * 1.25))]}
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip drilldownEnabled={drilldownEnabled} mode={mode} />} />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  formatter={(value) =>
                    value === 'primary'
                      ? (mode === 'rate' ? 'Unsub rate' : 'Unsubscribes')
                      : value === 'avgLine'
                        ? avgLegend
                        : value === 'sends'
                          ? 'Sends'
                          : value
                  }
                />
                {averageValue != null && (
                  <ReferenceLine
                    yAxisId="unsubs"
                    y={averageValue}
                    stroke="#94a3b8"
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                  />
                )}
                <Line
                  yAxisId="unsubs"
                  type="monotone"
                  dataKey="primary"
                  name="primary"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ r: 2.5, strokeWidth: 0, fill: '#f59e0b', cursor: drilldownEnabled ? 'pointer' : 'default' }}
                  activeDot={{
                    r: 5,
                    cursor: drilldownEnabled ? 'pointer' : 'default',
                    onClick: (_: any, payload: any) => {
                      const month = payload?.payload?.month as string | undefined;
                      if (month) void openDrilldown(month);
                    },
                  }}
                  onClick={(state: any) => {
                    const month = state?.payload?.month as string | undefined;
                    if (month) void openDrilldown(month);
                  }}
                  connectNulls={false}
                />
                <Line
                  yAxisId="unsubs"
                  type="monotone"
                  dataKey="avgLine"
                  name="avgLine"
                  stroke="#94a3b8"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  dot={false}
                  activeDot={false}
                  legendType="plainline"
                />
                <Line
                  yAxisId="sends"
                  type="monotone"
                  dataKey="sends"
                  name="sends"
                  stroke="#64748b"
                  strokeWidth={1.25}
                  strokeDasharray="2 4"
                  strokeOpacity={0.7}
                  dot={false}
                  activeDot={false}
                  legendType="plainline"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {drilldownEnabled && selectedMonth && (
            <div className="mt-3 rounded-md border border-amber-200 dark:border-amber-800/60 bg-amber-50/50 dark:bg-amber-900/10 px-3 py-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Top sends by volume
                    {drilldown?.label ? ` · ${drilldown.label}` : ''}
                  </p>
                  {drilldown && (
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                      {drilldown.monthTotal.toLocaleString()} unsubs this month
                      {drilldown.topVolumeShare > 0 && (
                        <> · top 3 = {drilldown.topVolumeShare}% of month</>
                      )}
                      {drilldown.unattributedCount > 0 && (
                        <> · {drilldown.unattributedCount.toLocaleString()} not linked to a send</>
                      )}
                      {drilldown.baselineRate != null && drilldown.thresholdRate != null && (
                        <> · rate elevated at ≥ {drilldown.thresholdRate.toFixed(2)}%</>
                      )}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedMonth(null);
                    setDrilldown(null);
                    setDrillError(null);
                  }}
                  className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
                  title="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {drillLoading && (
                <div className="flex items-center gap-2 text-xs text-gray-500 py-3">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading send breakdown…
                </div>
              )}
              {drillError && (
                <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                  {drillError}
                </div>
              )}
              {!drillLoading && !drillError && drilldown && (
                <>
                  {drilldown.message && (
                    <p className="text-xs text-amber-800 dark:text-amber-200/90 mb-2">
                      {drilldown.message}
                    </p>
                  )}
                  {drilldown.sends.length === 0 ? (
                    <p className="text-xs text-gray-600 dark:text-slate-300">
                      No send-linked unsubscribes to show for this month.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {drilldown.sends.map((s) => (
                        <li
                          key={s.statid}
                          className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Link
                                to={sendsLink(s.statid)}
                                className="font-medium text-indigo-600 dark:text-indigo-400 hover:underline truncate max-w-[28rem]"
                              >
                                {s.campaignName}
                              </Link>
                              {s.elevatedRate && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                  Elevated rate
                                </span>
                              )}
                            </div>
                            <span className="text-gray-500 dark:text-slate-400">
                              {s.unsubscribes.toLocaleString()} unsubs
                              {s.sendSize > 0 && <> / {s.sendSize.toLocaleString()} sent</>}
                              {s.zScore != null && s.elevatedRate && <> · z={s.zScore}</>}
                            </span>
                          </div>
                          <div className="text-right tabular-nums">
                            <div className="font-semibold text-amber-700 dark:text-amber-300">
                              {s.pctOfMonth.toFixed(1)}% of month
                            </div>
                            {s.sendSize > 0 && (
                              <div className="text-gray-400 dark:text-slate-500">
                                {s.rate.toFixed(2)}% rate
                                {s.baselineRate != null && <> vs {s.baselineRate.toFixed(2)}% avg</>}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
